// src/pages/NotificationsPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Card from "../ui/Card";
import { fetchOrgActivity } from "../utils/orgAudit";
import {
  dismissNotification,
  NOTIFICATION_READ_EVENT,
  getReadNotificationIds,
  handleNotificationTap,
  notificationReadStorageKey,
} from "../utils/notificationNavigation";
import {
  filterVisibleNotifications,
  getNotificationMeta,
  getNotificationTitle,
} from "../utils/notificationPrivacy";
import {
  claimFlagCase,
  fetchFlagCases,
  getFlagCaseStatusLabel,
  isFinalFlagStatus,
  resolveFlagCase,
} from "../utils/flagCases";

const toMillis = (value) => {
  if (!value) return Date.now();
  if (typeof value === "number") return value;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Date.now() : parsed;
};

const getClaimDisplay = (row) => {
  if (!row?.claimedBy) return "Unclaimed";
  return "Claimed";
};

export default function NotificationsPage() {
  const { user, activeOrgId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [readNotifIds, setReadNotifIds] = useState(() => new Set());
  const [tapFeedback, setTapFeedback] = useState("");
  const [flagCases, setFlagCases] = useState([]);
  const [flagStatus, setFlagStatus] = useState("");
  const [busyCaseId, setBusyCaseId] = useState("");

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    const orgId = activeOrgId || user.orgId || "org_default";
    fetchOrgActivity(user, orgId, 25)
      .then((activity) => {
        if (!mounted) return;
        setRows(Array.isArray(activity) ? activity : []);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [user, activeOrgId]);

  const orgId = activeOrgId || user?.orgId || "org_default";
  const role = String(user?.role || "").toLowerCase();
  const canHandleFlags = role === "coach" || role === "admin" || role === "superadmin";

  useEffect(() => {
    if (!user?.uid || !canHandleFlags) return undefined;
    let mounted = true;
    const refresh = async () => {
      const rows = await fetchFlagCases(user, orgId, { max: 40, includeResolved: true });
      if (!mounted) return;
      setFlagCases(Array.isArray(rows) ? rows : []);
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [user, orgId, canHandleFlags]);

  const notificationRows = useMemo(() => {
    if (!user?.uid) return [];
    return filterVisibleNotifications(rows, user, readNotifIds);
  }, [rows, user, readNotifIds]);
  const readKey = useMemo(
    () => notificationReadStorageKey(user?.uid, activeOrgId || user?.orgId || "org_default"),
    [user?.uid, user?.orgId, activeOrgId]
  );
  useEffect(() => {
    const hydrate = () => setReadNotifIds(getReadNotificationIds(readKey));
    const onRead = (event) => {
      const key = String(event?.detail?.readKey || "");
      if (key && key !== readKey) return;
      hydrate();
    };
    hydrate();
    window.addEventListener(NOTIFICATION_READ_EVENT, onRead);
    return () => window.removeEventListener(NOTIFICATION_READ_EVENT, onRead);
  }, [readKey]);
  useEffect(() => {
    if (!tapFeedback) return undefined;
    const id = window.setTimeout(() => setTapFeedback(""), 1800);
    return () => window.clearTimeout(id);
  }, [tapFeedback]);

  const unresolvedCases = useMemo(
    () => flagCases.filter((c) => !isFinalFlagStatus(c.status)),
    [flagCases]
  );

  const handleClaim = async (row) => {
    if (!user?.uid || !row?.id) return;
    setBusyCaseId(row.id);
    setFlagStatus("");
    const result = await claimFlagCase(user, orgId, row.id);
    if (!result?.ok) {
      setFlagStatus(result?.reason === "claimed" ? "Already claimed by another coach." : "Unable to claim case.");
    } else {
      setFlagStatus("Case claimed.");
    }
    const refreshed = await fetchFlagCases(user, orgId, { max: 40, includeResolved: true });
    setFlagCases(Array.isArray(refreshed) ? refreshed : []);
    setBusyCaseId("");
  };

  const handleResolve = async (row) => {
    if (!user?.uid || !row?.id) return;
    setBusyCaseId(row.id);
    setFlagStatus("");
    const result = await resolveFlagCase(user, orgId, row.id, { resolutionType: "resolved" });
    if (!result?.ok) {
      setFlagStatus(result?.reason === "claimed_by_other" ? "Claimed by another coach." : "Unable to resolve case.");
    } else {
      setFlagStatus(`Case updated: ${getFlagCaseStatusLabel({ status: result.status })}.`);
    }
    const refreshed = await fetchFlagCases(user, orgId, { max: 40, includeResolved: true });
    setFlagCases(Array.isArray(refreshed) ? refreshed : []);
    setBusyCaseId("");
  };

  const canResolveRow = (row) => {
    if (!row) return false;
    if (isFinalFlagStatus(row.status)) return false;
    if (role === "admin" || role === "superadmin") return true;
    if (role !== "coach") return false;
    return !row.claimedBy || row.claimedBy === user?.uid;
  };

  const handleNotificationRowTap = (row) => {
    const id = String(row?.id || "").trim();
    if (id) {
      setReadNotifIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
    setTapFeedback("Notification marked as read and removed from the unread list.");
    handleNotificationTap({
      navigate,
      row,
      readKey,
      currentPathname: location.pathname,
    });
  };

  const handleDismissNotification = (event, row) => {
    event.stopPropagation();
    dismissNotification(readKey, row);
    const id = String(row?.id || "").trim();
    if (id) {
      setReadNotifIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
    setTapFeedback("Notification dismissed.");
  };

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h2>Notifications</h2>
        <p>Latest pilot activity updates.</p>
      </div>
      <Card>
        {tapFeedback ? <div className="nia-muted" style={{ marginBottom: 10 }}>{tapFeedback}</div> : null}
        {notificationRows.length === 0 ? (
          <div className="nia-muted">No new notifications.</div>
        ) : (
          <div className="nia-list">
            {notificationRows.map((row) => (
              <div
                key={row.id || row.createdAt}
                className="nia-list-btn"
                style={{ gap: 10 }}
              >
                <button
                  type="button"
                  className="notif-row-main"
                  onClick={() => handleNotificationRowTap(row)}
                >
                  <span className="title">{getNotificationTitle(row, user)}</span>
                  <span className="nia-pill">{getNotificationMeta(row, user)}</span>
                </button>
                <button
                  type="button"
                  className="notif-dismiss-btn"
                  aria-label={`Dismiss notification: ${getNotificationTitle(row, user)}`}
                  title="Dismiss"
                  onClick={(event) => handleDismissNotification(event, row)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
      {canHandleFlags && (
        <Card title="Coach Review Cases">
          {flagStatus ? <div className="nia-muted" style={{ marginBottom: 10 }}>{flagStatus}</div> : null}
          {unresolvedCases.length === 0 ? (
            <div className="nia-muted">No unresolved review cases.</div>
          ) : (
            <div className="nia-list">
              {unresolvedCases.slice(0, 20).map((row) => (
                <div key={row.id} className="nia-list-btn" style={{ cursor: "default" }}>
                  <span className="title">
                    {row.id} • Session {row.sessionId || "-"}
                    <div className="nia-muted">
                      Created {row.createdAt ? new Date(toMillis(row.createdAt)).toLocaleString() : "now"}
                      {` • ${getClaimDisplay(row)}`}
                      {` • ${String(row.reviewKind || row.caseType || "flagged").replace(/_/g, " ")}`}
                    </div>
                    {row.reviewReason ? (
                      <div className="nia-muted">
                        Reason: {String(row.reviewReason).replace(/_/g, " ")}
                      </div>
                    ) : null}
                  </span>
                  <span className="nia-pill">{getFlagCaseStatusLabel(row)}</span>
                  {role === "coach" && ["open", "escalated"].includes(String(row.status || "").toLowerCase()) ? (
                    <button
                      className="btn btn-ghost"
                      disabled={busyCaseId === row.id}
                      onClick={() => handleClaim(row)}
                    >
                      {busyCaseId === row.id ? "Working..." : "Claim"}
                    </button>
                  ) : null}
                  {canResolveRow(row) ? (
                    <button
                      className="btn btn-ghost"
                      disabled={busyCaseId === row.id}
                      onClick={() => handleResolve(row)}
                    >
                      {busyCaseId === row.id ? "Working..." : "Resolve"}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
